import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Headphones, Send, CheckCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = '';  // Use relative URLs with proxy

const ContactSupport = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/feedback/support`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });

      if (response.data.success) {
        setSubmittedEmail(formData.email);
        setIsSubmitted(true);
        
        toast({
          title: "Message sent!",
          description: "We'll get back to you within 24-48 hours.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Support request error:', error);
      toast({
        title: "Failed to send",
        description: error.response?.data?.detail || "Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFCC00] rounded-full mb-6">
            <Headphones className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-5xl font-bold font-montserrat mb-4">
            Contact <span className="text-[#FFCC00]">Support</span>
          </h1>
          <p className="text-gray-400 text-lg">We&apos;re here to help. Get in touch with our team.</p>
        </div>

        {isSubmitted ? (
          /* Success Message */
          <Card className="bg-gray-800/50 border-gray-700 p-12 backdrop-blur-sm text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold font-montserrat mb-4">Message Received!</h2>
            <p className="text-gray-300 text-lg mb-6">
              Thank you for reaching out. Our support team will review your message and respond within 24-48 hours.
            </p>
            <p className="text-gray-400 mb-8">
              You&apos;ll receive a confirmation email at <strong>{submittedEmail}</strong>
            </p>
            <Button
              onClick={() => setIsSubmitted(false)}
              className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold"
            >
              Send Another Message
            </Button>
          </Card>
        ) : (
          /* Support Form */
          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Info Sidebar */}
            <div className="md:col-span-1 space-y-6">
              <Card className="bg-gray-800/50 border-gray-700 p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <Mail className="h-5 w-5 text-[#FFCC00] mr-2" />
                  <h3 className="font-semibold text-white">Email Us</h3>
                </div>
                <p className="text-gray-400 text-sm mb-2">General inquiries:</p>
                <a href="mailto:hello@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline text-sm">
                  hello@miccheckmillionaire.com
                </a>
                <Separator className="bg-gray-700 my-4" />
                <p className="text-gray-400 text-sm mb-2">Legal matters:</p>
                <a href="mailto:legal@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline text-sm">
                  legal@miccheckmillionaire.com
                </a>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700 p-6 backdrop-blur-sm">
                <h3 className="font-semibold text-white mb-4">Quick Links</h3>
                <div className="space-y-3">
                  <Link to="/terms" className="block text-gray-400 hover:text-[#FFCC00] transition-colors text-sm">
                    → Terms of Service
                  </Link>
                  <Link to="/privacy" className="block text-gray-400 hover:text-[#FFCC00] transition-colors text-sm">
                    → Privacy Policy
                  </Link>
                  <Link to="/conduct" className="block text-gray-400 hover:text-[#FFCC00] transition-colors text-sm">
                    → Code of Conduct
                  </Link>
                </div>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700 p-6 backdrop-blur-sm">
                <h3 className="font-semibold text-white mb-2">Response Time</h3>
                <p className="text-gray-400 text-sm">
                  We typically respond within <strong className="text-white">24-48 hours</strong> during business days.
                </p>
              </Card>
            </div>

            {/* Form */}
            <div className="md:col-span-2">
              <Card className="bg-gray-800/50 border-gray-700 p-8 backdrop-blur-sm">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500 h-12"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500 h-12"
                      required
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <Label htmlFor="subject" className="text-white mb-2 block">Subject *</Label>
                    <Select value={formData.subject} onValueChange={(value) => setFormData({ ...formData, subject: value })} required>
                      <SelectTrigger className="bg-gray-900/50 border-gray-600 text-white h-12">
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="general">General Question</SelectItem>
                        <SelectItem value="tech">Tech Issue</SelectItem>
                        <SelectItem value="report">Report User</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="legal">Legal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Please describe your issue or question in detail..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500 min-h-[200px]"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold h-12 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        )}

        {/* Footer Links */}
        <div className="mt-12 text-center space-x-6">
          <Link to="/terms" className="text-[#FFCC00] hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="text-[#FFCC00] hover:underline">Privacy Policy</Link>
          <Link to="/conduct" className="text-[#FFCC00] hover:underline">Code of Conduct</Link>
        </div>
      </div>
    </div>
  );
};

export default ContactSupport;